import { useState } from "react";
import { toast } from "sonner";
import { joinWaitlist, updateWaitlistDetails } from "@/lib/api";
import { ArrowRight, Check, Loader2, Apple, Smartphone } from "lucide-react";

const USE_CASES = [
  "Cycle syncing & hormone balance",
  "Fat loss / body recomposition",
  "Nutrition & macro tracking",
  "Symptom & mood tracking",
  "Fitness & recovery",
  "Perimenopause / menopause",
  "Postpartum wellness",
  "General wellness",
];

const SOURCES = [
  "Instagram",
  "TikTok",
  "Friend / word of mouth",
  "Google search",
  "Podcast",
  "Facebook",
  "Press / article",
  "Other",
];

export const WaitlistForm = ({
  variant = "light",
  source = "landing_page",
  testIdPrefix = "hero",
  placeholder = "Your email address",
  cta = "Claim $13/month",
}) => {
  const isDark = variant === "dark";
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // step 1
  const [email, setEmail] = useState("");
  const [entryId, setEntryId] = useState(null);
  const [alreadyComplete, setAlreadyComplete] = useState(false);

  // step 2
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [platform, setPlatform] = useState("");
  const [useCase, setUseCase] = useState("");
  const [referralSource, setReferralSource] = useState("");

  const handleEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email.");
      return;
    }
    setLoading(true);
    try {
      const data = await joinWaitlist(email, source);
      setEntryId(data.id);
      toast.success(data.message || "You're on the list.");
      if (data.details_completed) {
        setAlreadyComplete(true);
      } else {
        setStep(2);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDetails = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      toast.error("Please share your first and last name.");
      return;
    }
    if (!platform) {
      toast.error("Pick your phone — iOS or Android.");
      return;
    }
    setLoading(true);
    try {
      const data = await updateWaitlistDetails(entryId, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        platform,
        use_case: useCase || undefined,
        referral_source: referralSource || undefined,
      });
      toast.success(data.message || "You're all set.");
      setStep(3);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // ------- Step 1: email -------
  if (step === 1) {
    const wrapperClasses = isDark
      ? "flex flex-col sm:flex-row gap-2 p-2 rounded-full bg-[#F4ECFF]/10 border border-[#F4ECFF]/30 backdrop-blur-md"
      : "flex flex-col sm:flex-row gap-2 p-2 rounded-3xl sm:rounded-full bg-white/80 border border-[#E0D0F5] backdrop-blur-md shadow-sm";

    const inputClasses = isDark
      ? "flex-1 bg-transparent text-[#F4ECFF] placeholder-[#F4ECFF]/60 px-5 py-3 outline-none font-sans text-base rounded-full"
      : "flex-1 bg-transparent text-[#0B0A4A] placeholder-[#3E1A7A]/60 px-5 py-3 outline-none font-sans text-base rounded-full";

    return (
      <form onSubmit={handleEmail} className={wrapperClasses} data-testid={`${testIdPrefix}-waitlist-form`}>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={placeholder}
          className={inputClasses}
          data-testid={`${testIdPrefix}-waitlist-email-input`}
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading}
          className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#3E1A7A] hover:bg-[#0B0A4A] active:bg-[#0B0A4A] disabled:opacity-70 text-white px-6 sm:px-7 py-3 text-sm font-medium tracking-wide transition-colors duration-300"
          data-testid={`${testIdPrefix}-waitlist-submit-btn`}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Joining…</span>
            </>
          ) : alreadyComplete ? (
            <>
              <Check className="w-4 h-4" />
              <span>On the list</span>
            </>
          ) : (
            <>
              <span>{cta}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </>
          )}
        </button>
      </form>
    );
  }

  // ------- Step 2: details -------
  if (step === 2) {
    const fieldText = isDark ? "text-[#F4ECFF]" : "text-[#0B0A4A]";
    const fieldBg = isDark
      ? "bg-white/10 border-white/25 text-[#F4ECFF] placeholder-[#F4ECFF]/50"
      : "bg-white/80 border-[#E0D0F5] text-[#0B0A4A] placeholder-[#3E1A7A]/50";
    const cardBg = isDark
      ? "bg-[#F4ECFF]/5 border-[#F4ECFF]/20"
      : "bg-white/70 border-[#E0D0F5]";
    const labelClasses = `text-[10px] sm:text-xs uppercase tracking-[0.22em] font-medium ${isDark ? "text-[#EADDFF]/70" : "text-[#3E1A7A]"}`;

    return (
      <form
        onSubmit={handleDetails}
        className={`text-left p-5 sm:p-7 rounded-3xl border backdrop-blur-xl ${cardBg} animate-fade-up`}
        data-testid={`${testIdPrefix}-details-form`}
      >
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <p className={`${labelClasses}`} data-testid={`${testIdPrefix}-details-eyebrow`}>
              One more step
            </p>
            <h3 className={`font-serif text-2xl sm:text-3xl mt-2 leading-tight ${fieldText}`}>
              Let's personalise your founding access.
            </h3>
            <p className={`text-sm mt-2 ${isDark ? "text-[#F4ECFF]/70" : "text-[#0B0A4A]/70"}`}>
              Tell us a little about you so the app is ready when you are.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClasses}>First name</label>
            <input
              type="text"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Charlotte"
              className={`mt-2 w-full rounded-full px-5 py-3 border outline-none font-sans text-base focus:ring-2 focus:ring-[#3E1A7A]/40 ${fieldBg}`}
              data-testid={`${testIdPrefix}-first-name-input`}
              disabled={loading}
            />
          </div>
          <div>
            <label className={labelClasses}>Last name</label>
            <input
              type="text"
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Bryan"
              className={`mt-2 w-full rounded-full px-5 py-3 border outline-none font-sans text-base focus:ring-2 focus:ring-[#3E1A7A]/40 ${fieldBg}`}
              data-testid={`${testIdPrefix}-last-name-input`}
              disabled={loading}
            />
          </div>
        </div>

        <div className="mt-5">
          <label className={labelClasses}>What phone do you use?</label>
          <div className="grid grid-cols-2 gap-3 mt-2" data-testid={`${testIdPrefix}-platform-group`}>
            {[
              { value: "ios", label: "iPhone (iOS)", Icon: Apple },
              { value: "android", label: "Android", Icon: Smartphone },
            ].map(({ value, label, Icon }) => {
              const active = platform === value;
              return (
                <button
                  type="button"
                  key={value}
                  onClick={() => setPlatform(value)}
                  data-testid={`${testIdPrefix}-platform-${value}`}
                  className={`flex items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-medium border transition-all ${
                    active
                      ? "bg-[#3E1A7A] border-[#3E1A7A] text-white"
                      : isDark
                      ? "bg-white/5 border-white/25 text-[#F4ECFF] hover:border-[#3E1A7A]"
                      : "bg-white/60 border-[#E0D0F5] text-[#0B0A4A] hover:border-[#3E1A7A]"
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.75} />
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-5">
          <label className={labelClasses}>What will you use Feminine Ways for?</label>
          <select
            value={useCase}
            onChange={(e) => setUseCase(e.target.value)}
            className={`mt-2 w-full rounded-full px-5 py-3 border outline-none font-sans text-base appearance-none cursor-pointer focus:ring-2 focus:ring-[#3E1A7A]/40 ${fieldBg}`}
            data-testid={`${testIdPrefix}-use-case-select`}
            disabled={loading}
          >
            <option value="" className="text-[#0B0A4A]">Select what fits best…</option>
            {USE_CASES.map((u) => (
              <option key={u} value={u} className="text-[#0B0A4A]">
                {u}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-5">
          <label className={labelClasses}>How did you find us?</label>
          <select
            value={referralSource}
            onChange={(e) => setReferralSource(e.target.value)}
            className={`mt-2 w-full rounded-full px-5 py-3 border outline-none font-sans text-base appearance-none cursor-pointer focus:ring-2 focus:ring-[#3E1A7A]/40 ${fieldBg}`}
            data-testid={`${testIdPrefix}-referral-select`}
            disabled={loading}
          >
            <option value="" className="text-[#0B0A4A]">Choose a source…</option>
            {SOURCES.map((s) => (
              <option key={s} value={s} className="text-[#0B0A4A]">
                {s}
              </option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="mt-7 w-full group inline-flex items-center justify-center gap-2 rounded-full bg-[#3E1A7A] hover:bg-[#0B0A4A] disabled:opacity-70 text-white px-6 py-3.5 text-sm font-medium tracking-wide transition-colors duration-300"
          data-testid={`${testIdPrefix}-details-submit-btn`}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Saving…</span>
            </>
          ) : (
            <>
              <span>Lock in my founding access</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </>
          )}
        </button>
        <p className={`text-[11px] mt-3 text-center ${isDark ? "text-[#F4ECFF]/55" : "text-[#0B0A4A]/55"}`}>
          We'll only email you about launch & your founding rate.
        </p>
      </form>
    );
  }

  // ------- Step 3: confirmation -------
  return (
    <div
      className={`p-7 sm:p-9 rounded-3xl border backdrop-blur-xl text-center animate-fade-up ${
        isDark ? "bg-[#F4ECFF]/5 border-[#F4ECFF]/20 text-[#F4ECFF]" : "bg-white/70 border-[#E0D0F5] text-[#0B0A4A]"
      }`}
      data-testid={`${testIdPrefix}-success-card`}
    >
      <div className="mx-auto w-14 h-14 rounded-full bg-[#3E1A7A]/15 flex items-center justify-center">
        <Check className="w-6 h-6 text-[#3E1A7A]" strokeWidth={2.5} />
      </div>
      <h3 className="font-serif text-3xl sm:text-4xl mt-5 leading-tight">
        You're in, <span className="italic text-[#3E1A7A]">{firstName || "founder"}</span>.
      </h3>
      <p className={`mt-3 text-sm sm:text-base ${isDark ? "text-[#F4ECFF]/75" : "text-[#0B0A4A]/75"}`}>
        Your founding rate is locked in — $13/month for life, or just $10/month if you pay
        annually upfront. We'll email you the moment doors open on August 1, 2026.
      </p>
    </div>
  );
};

export default WaitlistForm;
